// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// The interactive tutorial: six steps, on the real board, driving the SHIPPED state machine.
//
// ── WHY IT IS DATA AND NOT A SECOND GAME ────────────────────────────────────────────────────────
// Every step's `perform` calls the same `Sim` methods a thumb calls — `summon`, `setPolicy`,
// `buildUnit`, `buyCar` — against the same simulation the player is looking at. There is no mock
// tower anywhere in this file, and that is deliberate: a tutorial with its own private implementation
// is a lie the moment anybody touches the rules, and it is not even a DETECTABLE lie. It would go on
// teaching the old game, confidently, for ever.
//
// Because it is data, `tests/tutorial.test.ts` can replay all six steps through the real rules and
// assert that each one is legal and reachable from the state the previous step produced. A rules
// change turns the tutorial red instead of silently teaching a game that no longer exists.
//
// ── THE SHAPE OF THE SEQUENCE ───────────────────────────────────────────────────────────────────
// Step 1 is one tap that obviously works. Steps 2 and 3 are the verb the whole game is built on, and
// step 3 is the first time the player is told they cannot save everyone. Step 5 is the first time the
// game argues back — the office you buy makes the next peak worse. Step 6 is the moment it becomes
// interesting: the obvious purchase does not double anything, because two cars in one shaft are on
// the same rails and cannot pass each other. That is a fact about the building nobody could have
// predicted from the rules, and the answer to it is the verb from step 2.

import { Sim } from './sim';
import { STOREYS } from './tuning';
import type { Dir } from './events';

/** Which interaction is live during a step. Everything else is refused. */
export type Allow = 'spine' | 'summon' | 'policy' | 'build' | 'car';

export interface TutorialStep {
  id: string;
  /** One sentence, imperative, naming the thing to touch. */
  copy: string;
  /** Other interactions this step also permits, when the move it asks for takes more than one tap. */
  also?: readonly Allow[];
  /** What the player learns once they have done it — shown after the step completes. */
  after: string;
  allow: Allow;
  /** Whether the asked-for move can be made right now. The run advances time until it can. */
  ready(sim: Sim): boolean;
  /** The move this step is asking for, made through the shipped API. */
  perform(sim: Sim): boolean;
}

/** The storey carrying the worst pressure right now — what steps 2 and 3 are pointing at. */
export function worstStorey(sim: Sim): number {
  let best = -1;
  let score = 0;
  for (let s = 0; s < STOREYS; s++) {
    const p = sim.storeyPressure(s);
    if (p.n === 0) continue;
    const v = p.worstTicks + p.n * 30;
    if (v > score) {
      score = v;
      best = s;
    }
  }
  return best;
}

const anyQueue = (sim: Sim, min: number): number => {
  for (let s = 0; s < STOREYS; s++) if (sim.storeyPressure(s).n >= min) return s;
  return -1;
};

const summonAt = (sim: Sim, storey: number): boolean => {
  if (storey < 0) return false;
  const q = sim.worstQueueOn(storey);
  return q ? sim.summon(q.riser, storey, q.dir as Dir) : false;
};

/**
 * Summon somebody, anybody — preferring the storey the step is pointing at.
 *
 * A summon legitimately REFUSES when a car is already seconds away: that is the whiff rule, and it is
 * what stops mashing being the optimal play. But a teaching step must not be able to dead-end on it,
 * so the step falls through to the next crowd rather than reporting that the player did nothing
 * wrong and nothing happened.
 */
const summonAnywhere = (sim: Sim, preferred: number): boolean => {
  if (summonAt(sim, preferred)) return true;
  for (let s = 0; s < STOREYS; s++) if (s !== preferred && summonAt(sim, s)) return true;
  return false;
};

export const STEPS: readonly TutorialStep[] = [
  {
    id: 'spine',
    copy: 'Tap the coloured strip down the left edge.',
    after: 'That strip is your building. One bar per storey, and it grows when people are kept waiting.',
    allow: 'spine',
    ready: () => true,
    perform: () => true,
  },
  {
    id: 'summon',
    copy: 'People are waiting. Tap the crowd on the concourse to send a car to them.',
    after: 'That is the whole job. The automatic controller is only ever doing its best.',
    allow: 'summon',
    ready: (sim) => anyQueue(sim, 2) >= 0,
    perform: (sim) => summonAnywhere(sim, anyQueue(sim, 2)),
  },
  {
    id: 'triage',
    copy: 'Now tap the storey that has been waiting longest.',
    after: 'You cannot rescue everyone. Choosing who waits is the game.',
    allow: 'summon',
    ready: (sim) => worstStorey(sim) >= 0,
    perform: (sim) => summonAnywhere(sim, worstStorey(sim)),
  },
  {
    id: 'policy',
    copy: 'Set the riser to run express, so it skips the bottom storeys.',
    after: 'The top of the building drains — and everything below the express floor now has nothing.',
    allow: 'policy',
    ready: (sim) => sim.tower.risers.length > 0,
    perform: (sim) => {
      const r = sim.tower.risers[0];
      if (!r) return false;
      sim.setPolicy(r, 'express', 4);
      return r.policy === 'express';
    },
  },
  {
    id: 'build',
    copy: 'Put the riser back to serving every storey, then build one office.',
    after: 'More tenants is more rent — and more people wanting the same lift. Growth is a cost.',
    allow: 'build',
    ready: (sim) => findOfficeSpot(sim) !== null,
    perform: (sim) => {
      const r = sim.tower.risers[0];
      if (r) sim.setPolicy(r, 'all');
      const spot = findOfficeSpot(sim);
      return spot ? !!sim.buildUnit('office', spot.storey, spot.col) : false;
    },
  },
  {
    id: 'car',
    copy: 'One car is not enough now. Cut a second riser in the gap on the right, and give it a car.',
    after:
      'Look at what that cost you: a riser eats two columns on EVERY storey it passes, for ever. Capacity is floor space. When you cannot afford any more of it, you turn a car round by hand instead — tap the crowd.',
    allow: 'build',
    also: ['car'],
    ready: (sim) => sim.cash >= 3000 && freeRiserCol(sim) >= 0,
    perform: (sim) => {
      const col = freeRiserCol(sim);
      if (col < 0) return false;
      const r = sim.buildRiser(Math.max(8, sim.tower.storeys), col);
      return !!r && !!sim.buyCar(r, false);
    },
  },
];

/** The first two-column run a riser could still be cut in — the service void, until it is used. */
function freeRiserCol(sim: Sim): number {
  for (let col = 0; col + 2 <= 12; col++) {
    if (sim.canBuildRiser(Math.max(8, sim.tower.storeys), col).ok) return col;
  }
  return -1;
}

/**
 * Somewhere to put the office — but never in the gap the LAST step needs.
 *
 * Step five builds and step six cuts a riser into the only two-column run left, so a greedy search
 * that starts at column zero and takes the first fit will happily eat that run and leave the final
 * step impossible. The tutorial would then teach five sixths of the game and stop, which is exactly
 * the kind of dead end a scripted sequence must never be allowed to reach.
 */
function findOfficeSpot(sim: Sim): { storey: number; col: number } | null {
  const keep = freeRiserCol(sim);
  for (let storey = 1; storey <= Math.min(6, sim.mode.maxStorey); storey++) {
    for (let col = 0; col + 3 <= 12; col++) {
      if (keep >= 0 && col < keep + 2 && col + 3 > keep) continue;
      if (sim.canBuildUnit('office', storey, col).ok) return { storey, col };
    }
  }
  return null;
}

/**
 * The tutorial's own simulation. It is an ordinary `Sim` on a fixed seed with a fat wallet, so the
 * steps that cost money are never blocked by the economy — the sequence is about the verbs, and a
 * teaching step that fails because the player is skint teaches the wrong thing.
 */
export function createTutorialSim(): Sim {
  const sim = new Sim('ascent', TUTORIAL_SEED);
  sim.cash = 12000;
  return sim;
}

export const TUTORIAL_SEED = 4242;

export interface TutorialRun {
  index: number;
  step(): TutorialStep | null;
  allows(kind: Allow): boolean;
  /** Tell the tutorial an action succeeded; advances if it was the one being asked for. */
  did(kind: Allow): void;
  done(): boolean;
  skip(): void;
  /** The last completed step's payoff line, for the UI to show. */
  lastAfter: string;
}

export function createRun(): TutorialRun {
  const run: TutorialRun = {
    index: 0,
    lastAfter: '',
    step: () => STEPS[run.index] ?? null,
    allows: (kind) => {
      const s = run.step();
      return !!s && (s.allow === kind || (s.also?.includes(kind) ?? false));
    },
    did: (kind) => {
      const s = run.step();
      if (!s || s.allow !== kind) return;
      run.lastAfter = s.after;
      run.index++;
    },
    done: () => run.index >= STEPS.length,
    skip: () => {
      run.index = STEPS.length;
    },
  };
  return run;
}
